import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { readPendingShortlist, clearPendingShortlist } from "@/lib/pendingIntent";
import { useShortlistWriter } from "@/lib/useShortlistWriter";

export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { addToShortlist: writeShortlist, bustShortlistCaches } = useShortlistWriter();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const session_id = params.get("session_id");
    if (!session_id) { nav("/", { replace: true }); return; }
    (async () => {
      try {
        await api.post("/auth/session", { session_id });
        // Populate the AuthProvider's in-memory user state.
        await refresh();

        // Replay any pending "Add to shortlist" intents captured before sign-in.
        const pending = readPendingShortlist();
        if (pending.length) {
          clearPendingShortlist();
          const added = [];
          for (const item of pending) {
            try {
              await writeShortlist({
                university_id: item.university_id,
                degree_id: item.degree_id,
              });
              added.push(item.university_name);
            } catch {}
          }
          if (added.length === 1) {
            toast.success(`Signed in and saved ${added[0]} to your shortlist`);
          } else if (added.length > 1) {
            toast.success(`Signed in and saved ${added.length} universities to your shortlist`);
          }
          // `writeShortlist` already busts per-call; this is a belt-
          // and-braces reset for the empty-loop / all-failed case.
          bustShortlistCaches();
        }

        let returnTo = "/shortlist";
        try {
          const stored = sessionStorage.getItem("wua_return_to");
          if (stored && stored !== "/" && !stored.includes("session_id=")) returnTo = stored;
          sessionStorage.removeItem("wua_return_to");
        } catch {}
        nav(returnTo, { replace: true });
      } catch {
        toast.error("Sign-in failed. Please try again.");
        nav("/", { replace: true });
      }
    })();
  }, [nav, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="label-uppercase">Signing you in…</div>
        <div className="font-editorial text-2xl mt-2">Just a moment.</div>
      </div>
    </div>
  );
}
